import { formatPhone, normalizePhone } from '@fundline/shared/phone';
import { Link } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { SelectField, TextField } from '../../components/ui/Field.tsx';
import { Card, EmptyState, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { formatFull } from '../inbox/format.ts';
import { useMembersQuery } from '../members/use-members.ts';
import { SettingsTabs } from '../settings/SettingsTabs.tsx';
import {
  describeSource,
  liftedOnlyByMerchant,
  REASON_LABELS,
  SUPPRESSION_REASONS,
  type SuppressionEntry,
  type SuppressionFilters,
  useSuppressionMutations,
  useSuppressionQuery,
} from './use-suppression.ts';

/**
 * Settings → Suppression (PLAN.md §7, M6). Every number that must not get texts, with or without a
 * merchant: opt-outs, carrier blocks, review holds and manual do-not-contact. Admins add and release
 * (with a note); opt-outs and carrier blocks are lifted only by the merchant's START.
 */
export function SuppressionPage() {
  const [filters, setFilters] = useState<SuppressionFilters>({
    status: 'active',
    reason: 'any',
    phone: '',
  });
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Workspace settings"
        description="Numbers that get no texts from this workspace. Every change is in the audit log."
      />
      <SettingsTabs />
      <AddEntry />
      <Card title="Suppression list">
        <div className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <SelectField
              label="Show"
              value={filters.status}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  status: e.target.value === 'released' ? 'released' : 'active',
                })
              }
              options={[
                { value: 'active', label: 'Active (blocking texts)' },
                { value: 'released', label: 'Released' },
              ]}
            />
            <SelectField
              label="Reason"
              value={filters.reason}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  reason: SUPPRESSION_REASONS.find((r) => r === e.target.value) ?? 'any',
                })
              }
              options={[
                { value: 'any', label: 'Any reason' },
                ...Object.entries(REASON_LABELS).map(([value, label]) => ({ value, label })),
              ]}
            />
            <TextField
              label="Phone contains"
              inputMode="tel"
              value={filters.phone}
              onChange={(e) => setFilters({ ...filters, phone: e.target.value })}
              placeholder="555 0123"
            />
          </div>
          <EntryList filters={filters} />
        </div>
      </Card>
    </div>
  );
}

function AddEntry() {
  const { workspaceId } = useWorkspaceClaims();
  const { add } = useSuppressionMutations(workspaceId);
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const parsed = phone.trim() === '' ? null : normalizePhone(phone);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setDone(null);
    if (!parsed?.ok) {
      setError(parsed ? parsed.reason : 'Enter a phone number');
      return;
    }
    setError(null);
    add.mutate(
      { phone: parsed.e164, note },
      {
        onSuccess: (added) => {
          setDone(
            added
              ? `${formatPhone(parsed.e164)} is now marked do not contact.`
              : `${formatPhone(parsed.e164)} was already marked do not contact.`,
          );
          setPhone('');
          setNote('');
        },
      },
    );
  }

  return (
    <Card title="Mark a number do not contact">
      <form className="flex flex-col gap-3" onSubmit={onSubmit} noValidate>
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField
            label="Phone number"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={error}
            hint={
              parsed?.ok ? `Will block ${formatPhone(parsed.e164)}` : 'Any number, merchant or not.'
            }
          />
          <TextField
            label="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            placeholder="Asked us by phone not to text"
          />
        </div>
        {add.isError ? <Alert tone="error">{add.error.message}</Alert> : null}
        {done ? <Alert tone="success">{done}</Alert> : null}
        <div>
          <Button type="submit" busy={add.isPending}>
            Mark do not contact
          </Button>
        </div>
      </form>
    </Card>
  );
}

function EntryList({ filters }: { filters: SuppressionFilters }) {
  const { workspaceId } = useWorkspaceClaims();
  const list = useSuppressionQuery(workspaceId, filters);
  const members = useMembersQuery(workspaceId);
  const names = new Map((members.data ?? []).map((m) => [m.userId, m.fullName || m.email]));

  if (list.isPending) return <Spinner label="Loading the suppression list" />;
  if (list.isError) return <Alert tone="error">{list.error.message}</Alert>;
  const entries = list.data.pages.flat();
  if (entries.length === 0) {
    return (
      <EmptyState
        title={filters.status === 'active' ? 'No number is blocked' : 'Nothing released yet'}
      >
        {filters.status === 'active'
          ? 'Opt-outs, carrier blocks, review holds and numbers your team marks appear here.'
          : 'Entries an admin released, or the merchant lifted by texting START, appear here.'}
      </EmptyState>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <ul className="divide-y divide-hairline" data-testid="suppression-list">
        {entries.map((entry) => (
          <EntryRow key={entry.id} entry={entry} names={names} />
        ))}
      </ul>
      {list.hasNextPage ? (
        <div>
          <Button
            variant="secondary"
            busy={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          >
            Load more
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** One entry with its release action (admins). Also used on the merchant profile. */
export function EntryRow({
  entry,
  names,
}: {
  entry: SuppressionEntry;
  names: ReadonlyMap<string, string>;
}) {
  const { workspaceId } = useWorkspaceClaims();
  const { release } = useSuppressionMutations(workspaceId);
  const [releasing, setReleasing] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  function onRelease(e: FormEvent) {
    e.preventDefault();
    if (note.trim() === '') {
      setError('Say why this number may be texted again');
      return;
    }
    setError(null);
    release.mutate(
      { id: entry.id, note: note.trim() },
      { onError: (err) => setError(err.message), onSuccess: () => setReleasing(false) },
    );
  }

  const by = entry.createdBy ? (names.get(entry.createdBy) ?? 'A former member') : null;
  return (
    <li className="flex flex-col gap-1 py-3" data-testid="suppression-entry">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-ink">{formatPhone(entry.phone)}</span>
        <span className={`fl-badge ${entry.active ? 'fl-badge--declined' : 'fl-badge--neutral'}`}>
          {REASON_LABELS[entry.reason]}
        </span>
      </div>
      {entry.merchantId ? (
        <Link
          to="/merchants/$merchantId"
          params={{ merchantId: entry.merchantId }}
          className="callout text-ink-muted underline"
        >
          {entry.merchantName ?? 'Merchant profile'}
        </Link>
      ) : (
        <span className="callout text-ink-muted">No merchant with this number</span>
      )}
      <p className="footnote text-ink-muted">
        {describeSource(entry.source)}
        {by ? ` · ${by}` : ''} · {formatFull(entry.createdAt)}
      </p>
      {entry.note ? <p className="break-words callout text-ink-muted">{entry.note}</p> : null}
      {!entry.active ? (
        <p className="footnote text-ink-muted">
          Released {entry.releasedAt ? formatFull(entry.releasedAt) : ''}
          {entry.releasedBy
            ? ` by ${names.get(entry.releasedBy) ?? 'a former member'}`
            : ' (merchant texted START)'}
          {entry.releaseNote ? `: ${entry.releaseNote}` : ''}
        </p>
      ) : liftedOnlyByMerchant(entry.reason) ? (
        <p className="footnote text-ink-muted">
          Only the merchant can lift this, by texting START.
        </p>
      ) : releasing ? (
        <form className="mt-1 flex flex-col gap-2" onSubmit={onRelease} noValidate>
          <TextField
            label="Why may this number be texted again?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            error={error}
            maxLength={500}
            autoFocus
          />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="danger" busy={release.isPending}>
              Release
            </Button>
            <Button variant="ghost" onClick={() => setReleasing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div>
          <Button variant="secondary" size="sm" onClick={() => setReleasing(true)}>
            Release…
          </Button>
        </div>
      )}
    </li>
  );
}
